import { Spinner } from '@lovoldsystem/ui'
import { Stack, useTheme } from '@mui/material'
import { useEffect, useState } from 'react'
import { Navigate, Route, Routes } from 'react-router-dom'
import { axios } from 'src/axios'
import { RoleTypes, SystemPortal } from 'src/enums'
import { useDialogCtx, useUserCtx } from 'src/hooks/context-hooks'
import BackgroundJobsScreen from 'src/screens/admin/background-jobs/BackgroundJobsScreen'
import DataSyncScreen from 'src/screens/admin/data-sync/DataSyncScreen'
import EquipmentMigrationScreen from 'src/screens/admin/equipment-migration/EquipmentMigrationScreen'
import AdminNewsScreen from 'src/screens/admin/news/AdminNewsScreen'
import DeviationsPage from 'src/screens/customer/DeviationsPage'
import AllUsers from 'src/screens/customer/users/AllUsers'
import { AppRoutesNavigation, AppRoutesRouter } from '../../app-routes'
import { permissionBinding, permissionLevel } from '../../constant'
import { UserRoleGuard } from '../../guards/UserRoleGuard'
import { SystemAdminDocumentCategories } from '../../pages/document-category/SystemAdminDocumentCategories'
import { AuditLogsContainer } from '../../screens/admin/audit-logs/AuditLogsContainer'
import { CustomPropertyContainer } from '../../screens/admin/custom-type/CustomPropertyContainer'
import { AdminCustomersScreen } from '../../screens/admin/customers/AdminCustomersScreen'
import { DistributorContainer } from '../../screens/admin/distributor/DistributorContainer'
import { GlobalDocumentAdminContainer } from '../../screens/admin/global-documents/GlobalDocumentAdminContainer'
import { LicenseContainer } from '../../screens/admin/license/LicenseContainer'
import { MainComponentTypesContainer } from '../../screens/admin/main-component-types/AdminMainComponentTypesContainer'
import { MaterialFactorScreen } from '../../screens/admin/material-factor/materialFactorScreen'
import { PositionDrawingScreen } from '../../screens/admin/position-drawing/positionDrawingScreen'
import { ProductCategoryContainer } from '../../screens/admin/product-category/ProductCategoryContainer'
import { ProductContainer } from '../../screens/admin/product/ProductContainer'
import { SensorTypeContainer } from '../../screens/admin/sensor-type/SensorTypeContainer'
import { SupplierContainer } from '../../screens/admin/supplier/SupplierContainer'
import { TimeReportTypeContainer } from '../../screens/admin/time-report-type/TimeReportTypeContainer'
import { UserResources } from '../../screens/customer/users/UserResources'
import { UsersContainer } from '../../screens/customer/users/UsersContainer'
import { DeviationContainerAdmin } from '../../screens/localities/deviations/DeviationContainer'
import { invalidateCustomer } from 'src/api/customer'
import { AccessControls } from '@lovoldsystem/authorization.client'
import AdminFeatureFlagsScreen from 'src/screens/admin/feature-flags/AdminFeatureFlagsScreen'

export const AdminRoutes = () => {
  const { user, isLoadingUser } = useUserCtx()
  const [permissions, setPermissions] = useState(-1)
  const theme = useTheme()
  const { showDialog } = useDialogCtx()
  const { setActivePortal } = useUserCtx()

  useEffect(() => {
    setActivePortal(SystemPortal.System)
  }, [])

  useEffect(() => {
    if (user) {
      axios.defaults.headers['X-Permissions'] = user.encryptedPermission
      setPermissions(user.permission)
    }
  }, [user])

  if (isLoadingUser || permissions == -1) return <Spinner size={'small'} />

  return (
    <Stack
      style={{ backgroundColor: theme.palette.backgroundMain[theme.palette.mode] }}
      width={'100%'}
      minHeight={'calc(100vh - 160px)'}
      className="pb-4-core"
    >
      <UserRoleGuard isNavigateToUnAuthorized>
        <Routes>
          <Route path={AppRoutesRouter.Admin.customers} element={<AdminCustomersScreen />} />
          <Route path={AppRoutesRouter.Admin.customerUsers} element={<UsersContainer isAdminLayout={true} />} />
          <Route path={AppRoutesRouter.Admin.customerUserResources} element={<UserResources isAdminLayout={true} />} />
          <Route path="*" element={<Navigate to={AppRoutesNavigation.Admin.customers} />} />
          <Route path={AppRoutesRouter.Admin.materialFactor} element={<MaterialFactorScreen />} />
          <Route path={AppRoutesRouter.Admin.deviations} element={<DeviationsPage />} />
          <Route path={AppRoutesRouter.Admin.users} element={<AllUsers />} />
          <Route
            path={AppRoutesRouter.Admin.accessControl}
            element={
              <AccessControls
                roleTypes={RoleTypes}
                permissionLevels={permissionLevel}
                isAdminUser={true}
                permissionBinding={permissionBinding}
                showDialog={showDialog}
                theme={theme}
                height={window.innerHeight - 250 + 'px'}
                handleInvalidations={() => {
                  invalidateCustomer.getUserRoles()
                }}
              />
            }
          />
          <Route path={AppRoutesRouter.Admin.deviation} element={<DeviationContainerAdmin />} />
          <Route path={AppRoutesRouter.Admin.productCategory} element={<ProductCategoryContainer />} />
          <Route path={AppRoutesRouter.Admin.product} element={<ProductContainer />} />
          <Route path={AppRoutesRouter.Admin.supplier} element={<SupplierContainer />} />
          <Route path={AppRoutesRouter.Admin.sensorType} element={<SensorTypeContainer />} />
          <Route path={AppRoutesRouter.Admin.distributor} element={<DistributorContainer />} />
          <Route path={AppRoutesRouter.Admin.positionDrawing} element={<PositionDrawingScreen />} />
          <Route path={AppRoutesRouter.Admin.documentCategory} element={<SystemAdminDocumentCategories />} />
          <Route path={AppRoutesRouter.Admin.auditLogs} element={<AuditLogsContainer />} />
          <Route
            path={AppRoutesRouter.Admin.customProperty}
            element={<CustomPropertyContainer isSuperAdmin={true} />}
          />
          <Route path={AppRoutesRouter.Admin.mainComponentTypes} element={<MainComponentTypesContainer />} />
          <Route path={AppRoutesRouter.Admin.globalDocuments} element={<GlobalDocumentAdminContainer />} />
          <Route path={AppRoutesRouter.Admin.timeReportTypes} element={<TimeReportTypeContainer />} />
          <Route path={AppRoutesRouter.Admin.license} element={<LicenseContainer />} />
          <Route path={AppRoutesRouter.Admin.news} element={<AdminNewsScreen />} />
          <Route path={AppRoutesRouter.Admin.featureFlags} element={<AdminFeatureFlagsScreen />} />
          <Route path={AppRoutesRouter.Admin.equipmentMigration} element={<EquipmentMigrationScreen />} />
          <Route path={AppRoutesRouter.Admin.dataSync} element={<DataSyncScreen />} />
          <Route path={AppRoutesRouter.Admin.backgroundJobs} element={<BackgroundJobsScreen />} />
        </Routes>
      </UserRoleGuard>
    </Stack>
  )
}
